import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { useFailures, useFilterOptions } from '@/hooks/useFailures';
import { useWeibullAnalysis, useCensoredWeibullAnalysis } from '@/hooks/useWeibullAnalysis';
import { useWorkspaceContext } from '@/hooks/useWorkspaceContext';
import { useLayout } from '@/contexts';
import { FailureRecord } from '@/types';
import { FailureFilters, DEFAULT_FAILURE_FILTERS } from '@/types/filters';
import { Workspace } from '@/types/workspace';
import { WorkspaceContext } from '@/types/workspaceContext';
import { WeibullAnalysisPanel, DualPlotView } from '@/components/weibull';
import { SidebarController } from '@/components/analysis/SidebarController';
import { ColorByOption, MarkerByOption, SizeByOption } from '@/components/analysis/PlotCustomization';
import { SaveWorkspaceModal, LoadWorkspaceModal } from '@/components/workspaces';
import { PageToolbar, ToolbarIcons, WaveLoading, StatsCard, TableCard, ResponsiveGrid, ParameterGroupCard } from '@/components/ui';
import ColumnHeader from '@/components/table/ColumnHeader';
import { useKeyboardShortcuts } from '@/hooks/useKeyboardShortcuts';
import { usePermissions } from '@/hooks/usePermissions';
import { MinimalWorkspace } from '@/utils/workspacePermissions';
import WorkspaceErrorBoundary from '@/components/error/WorkspaceErrorBoundary';
import { isCensoredObservation } from '@/utils/weibullCensored';
import { ChevronDownIcon, ChartBarIcon } from '@heroicons/react/24/outline';
import { externalLinks } from '@/config/externalLinks';
import { createWorkspaceContextFromLoaded } from '@/hooks/useWorkspaceContext';

/**
 * Analysis Page - Main Weibull analysis interface with workspace management
 *
 * WORKSPACE BEHAVIOR:
 * - Changes are tracked but NOT auto-saved
 * - Users must explicitly save to persist modifications
 * - "New Workspace" creates fresh state with no filters
 * - Load options: "Snapshot" (exact historical) vs "Recalculate" (fresh data)
 * - All data points included in analysis (not just paginated table view)
 */
const Analysis: React.FC = () => {
  const { getContentAreaStyle, shouldShowDualPlots } = useLayout();
  const [filters, setFilters] = useState<FailureFilters>(DEFAULT_FAILURE_FILTERS);
  const [showConfidenceIntervals, setShowConfidenceIntervals] = useState(true);
  const [confidenceLevel, setConfidenceLevel] = useState(95);
  const [showCensoredData, setShowCensoredData] = useState(true);
  const [selectedMethod, setSelectedMethod] = useState<'johnson' | 'mle'>('mle');
  const location = useLocation();

  // Workspace modal states
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [showLoadModal, setShowLoadModal] = useState(false);

  // Permissions
  const permissions = usePermissions();

  // Plot type state
  const [plotType, setPlotType] = useState<'probability' | 'cdf'>('probability');
  const [showDualPlots, setShowDualPlots] = useState(false);

  // Plot customization state
  const [colorBy, setColorBy] = useState<ColorByOption>('none');
  const [markerBy, setMarkerBy] = useState<MarkerByOption>('none');
  const [sizeBy, setSizeBy] = useState<SizeByOption>('uniform');
  const [showLegend, setShowLegend] = useState(false);

  // Check if we should show dual plots
  useEffect(() => {
    const checkDualPlots = () => {
      setShowDualPlots(shouldShowDualPlots());
    };

    checkDualPlots();
    window.addEventListener('resize', checkDualPlots);
    return () => window.removeEventListener('resize', checkDualPlots);
  }, [shouldShowDualPlots]);

  // Workspace context management (single source of truth)
  const [workspaceContext, workspaceContextActions] = useWorkspaceContext();

  const [preloadedWorkspace, setPreloadedWorkspace] = useState<Workspace>(() => {
    if (location.state?.workspace) {
      return location.state?.workspace as Workspace
    }
    return null;
  });

  const [currentTablePage, setCurrentTablePage] = useState(1);
  const tablePageSize = 20;

  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());

  const [tableFilters, setTableFilters] = useState({
    sites: [] as string[],
    turbineModels: [] as string[],
    componentsMajor: [] as string[],
    componentsMinor: [] as string[],
    failureModes: [] as string[],
    outcomeName: [] as string[],
    majorCorrective: [] as string[],
    plannedReactive: [] as string[],
    operationTimeMin: undefined as number | undefined,
    operationTimeMax: undefined as number | undefined,
    dateFrom: undefined as string | undefined,
    dateTo: undefined as string | undefined,
  });

  const [sortConfig, setSortConfig] = useState<{
    key: string;
    direction: 'asc' | 'desc';
  } | null>(null);

  const handleLoadWorkspace = useCallback(async (
    workspace: Workspace,
    loadedWorkspaceContext: WorkspaceContext
  ) => {
    try {
      setFilters(workspace.configuration.filters);

      setShowConfidenceIntervals(workspace.configuration.analysisParams.showConfidenceIntervals);
      setConfidenceLevel(workspace.configuration.analysisParams.confidenceLevel);
      setShowCensoredData(workspace.configuration.analysisParams.showCensoredData);

      workspaceContextActions.setWorkspaceContext(loadedWorkspaceContext);

    } catch (error) {
      console.error('Failed to load workspace:', error);
      console.error('Error details:', error);
    }
  }, [workspaceContextActions]);

  useEffect(() => {
    try {
      if (preloadedWorkspace) {
        // Open the regular load modal when coming from Dashboard
        const context = createWorkspaceContextFromLoaded(preloadedWorkspace);
        handleLoadWorkspace(preloadedWorkspace, context);
        setPreloadedWorkspace(null);
      }
    } catch (error) {
      console.error('Error in mount useEffect:', error);
    }
  }, [preloadedWorkspace, handleLoadWorkspace]);

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentTablePage(1);
  }, [filters]);

  // Query for complete failure dataset (not paginated)
  const completeFilters = {
    ...filters,
    excludedCaseIds: [], // Get all data for complete dataset
    size: 10000, // Get all available data
    page: 1,
  };

  const {
    data: failuresData,
    isLoading: isLoadingFailures,
    error: failuresError,
  } = useFailures(completeFilters);

  const { data: apiFilterOptions, isLoading: isLoadingOptions } = useFilterOptions();

  const {
    analysis: weibullAnalysis,
    isLoading: isWeibullLoading,
    hasEnoughData,
    sampleSize,
    metadata,
    diversityMetrics,
  } = useWeibullAnalysis(
    {
      ...filters,
      size: 10000, // Ensure Weibull analysis uses ALL data
      page: 1,
    },
    confidenceLevel / 100,
    selectedMethod
  );

  // Get censored analysis data for visualization
  const { analysis: censoredAnalysis } = useCensoredWeibullAnalysis(
    {
      ...filters,
      size: 10000,
      page: 1,
    },
    confidenceLevel / 100
  );

  // Complete failure dataset (for workspace saving and analysis)
  const completeFailures = useMemo(() => {
    return failuresData?.failures.filter((f) => !isCensoredObservation(f)) || [];
  }, [failuresData?.failures]);

  // Apply table-only filtering and sorting (doesn't affect analysis)
  const filteredAndSortedFailures = useMemo(() => {
    let filtered = [...completeFailures];

    // Apply table filters (for display only)
    if (tableFilters.sites.length > 0) {
      filtered = filtered.filter((f) => tableFilters.sites.includes(f.site_name));
    }
    if (tableFilters.turbineModels.length > 0) {
      filtered = filtered.filter((f) => tableFilters.turbineModels.includes(f.turbine_model));
    }
    if (tableFilters.componentsMajor.length > 0) {
      filtered = filtered.filter((f) => tableFilters.componentsMajor.includes(f.component_major));
    }
    if (tableFilters.componentsMinor.length > 0) {
      filtered = filtered.filter(
        (f) => f.component_minor && tableFilters.componentsMinor.includes(f.component_minor)
      );
    }
    if (tableFilters.failureModes.length > 0) {
      filtered = filtered.filter((f) => tableFilters.failureModes.includes(f.failure_mode));
    }
    if (tableFilters.outcomeName.length > 0) {
      filtered = filtered.filter((f) => tableFilters.outcomeName.includes(f.outcome_name));
    }
    if (tableFilters.majorCorrective.length > 0) {
      filtered = filtered.filter((f) => tableFilters.majorCorrective.includes(f.major_corrective));
    }
    if (tableFilters.plannedReactive.length > 0) {
      filtered = filtered.filter((f) => tableFilters.plannedReactive.includes(f.planned_reactive));
    }
    if (tableFilters.operationTimeMin !== undefined) {
      filtered = filtered.filter((f) => f.operation_time_years >= tableFilters.operationTimeMin!);
    }
    if (tableFilters.operationTimeMax !== undefined) {
      filtered = filtered.filter((f) => f.operation_time_years <= tableFilters.operationTimeMax!);
    }
    if (tableFilters.dateFrom) {
      filtered = filtered.filter(
        (f) => new Date(f.failure_date) >= new Date(tableFilters.dateFrom!)
      );
    }
    if (tableFilters.dateTo) {
      filtered = filtered.filter((f) => new Date(f.failure_date) <= new Date(tableFilters.dateTo!));
    }

    // Apply sorting
    if (sortConfig) {
      filtered.sort((a, b) => {
        let aValue: string | number | Date;
        let bValue: string | number | Date;

        switch (sortConfig.key) {
          case 'caseId':
            // Natural/alphanumeric sorting
            return a.case_id.toString().localeCompare(b.case_id.toString(), undefined, {
              numeric: true,
              sensitivity: 'base'
            }) * (sortConfig.direction === 'asc' ? 1 : -1);
          case 'turbine':
            aValue = a.turbine_name;
            bValue = b.turbine_name;
            break;
          case 'site':
            aValue = a.site_name;
            bValue = b.site_name;
            break;
          case 'component':
            aValue = a.component_major + (a.component_minor || '');
            bValue = b.component_major + (b.component_minor || '');
            break;
          case 'failureMode':
            aValue = a.failure_mode;
            bValue = b.failure_mode;
            break;
          case 'type':
            aValue = a.outcome_name;
            bValue = b.outcome_name;
            break;
          case 'majorCorrective':
            aValue = a.major_corrective;
            bValue = b.major_corrective;
            break;
          case 'plannedReactive':
            aValue = a.planned_reactive;
            bValue = b.planned_reactive;
            break;
          case 'operationTime':
            aValue = a.operation_time_years;
            bValue = b.operation_time_years;
            break;
          case 'failureDate':
            aValue = new Date(a.failure_date);
            bValue = new Date(b.failure_date);
            break;
          default:
            return 0;
        }

        if (aValue < bValue) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }

    return filtered;
  }, [completeFailures, tableFilters, sortConfig]);

  // Paginated failures for table display (shows filtered and sorted cases)
  const paginatedFailures = useMemo(() => {
    const start = (currentTablePage - 1) * tablePageSize;
    return filteredAndSortedFailures.slice(start, start + tablePageSize);
  }, [filteredAndSortedFailures, currentTablePage, tablePageSize]);

  // Table pagination metadata (counts ALL cases)
  const tablePaginationData = useMemo(() => {
    const excludedCaseIds = filters.excludedCaseIds || [];
    const includedCount = filteredAndSortedFailures.filter(
      (f) => !excludedCaseIds.includes(f.case_id.toString())
    ).length;

    return {
      totalCount: filteredAndSortedFailures.length, // Total filtered cases in table
      includedCount, // Cases included in analysis (not excluded)
      excludedCount: filteredAndSortedFailures.length - includedCount, // Cases excluded from analysis
      totalPages: Math.ceil(filteredAndSortedFailures.length / tablePageSize),
      hasMore: currentTablePage < Math.ceil(filteredAndSortedFailures.length / tablePageSize),
      currentPage: currentTablePage,
      pageSize: tablePageSize,
    };
  }, [filteredAndSortedFailures, filters.excludedCaseIds, currentTablePage, tablePageSize]);

  const handleFiltersChange = (newFilters: FailureFilters) => {
    setFilters(newFilters);
    // Mark workspace context as changed
    workspaceContextActions.markWorkspaceAsChanged();
  };

  const handleToggleFailure = (failureId: string, caseId: string | number) => {
    const caseIdStr = caseId.toString();
    const currentExcluded = filters.excludedCaseIds || [];
    const isCurrentlyExcluded = currentExcluded.includes(caseIdStr);

    if (isCurrentlyExcluded) {
      // Remove from excluded list (select it)
      handleFiltersChange({
        ...filters,
        excludedCaseIds: currentExcluded.filter((id) => id !== caseIdStr),
      });
    } else {
      // Add to excluded list (deselect it)
      handleFiltersChange({
        ...filters,
        excludedCaseIds: [...currentExcluded, caseIdStr],
      });
    }
  };

  const handleSelectFailure = (failureId: string) => {
    const failure = completeFailures.find((f) => f.failure_id === failureId);
    if (failure) {
      handleToggleFailure(failureId, failure.case_id);
    }
  };

  const handleSelectAll = () => {
    const currentExcluded = filters.excludedCaseIds || [];
    const allCaseIds = Array.from(new Set(filteredAndSortedFailures.map((f) => f.case_id.toString())));
    const allExcluded = allCaseIds.every((id) => currentExcluded.includes(id));

    if (allExcluded) {
      // All visible are excluded, so select all (remove from excluded)
      handleFiltersChange({
        ...filters,
        excludedCaseIds: currentExcluded.filter((id) => !allCaseIds.includes(id)),
      });
    } else {
      // Some or none are excluded, so exclude all visible
      handleFiltersChange({
        ...filters,
        excludedCaseIds: Array.from(new Set([...currentExcluded, ...allCaseIds])),
      });
    }
  };

  const handleSelectAllInPage = () => {
    const currentExcluded = filters.excludedCaseIds || [];
    const visibleCaseIds = Array.from(new Set(paginatedFailures.map((f) => f.case_id.toString())));
    const allVisibleExcluded = visibleCaseIds.every((id) => currentExcluded.includes(id));

    if (allVisibleExcluded) {
      // All visible are excluded, so select all (remove from excluded)
      handleFiltersChange({
        ...filters,
        excludedCaseIds: currentExcluded.filter((id) => !visibleCaseIds.includes(id)),
      });
    } else {
      // Some or none are excluded, so exclude all visible
      handleFiltersChange({
        ...filters,
        excludedCaseIds: Array.from(new Set([...currentExcluded, ...visibleCaseIds])),
      });
    }
  };

  // Toggle row expansion
  const toggleRowExpansion = (failureId: string) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(failureId)) {
      newExpanded.delete(failureId);
    } else {
      newExpanded.add(failureId);
    }
    setExpandedRows(newExpanded);
  };

  // Expand/Collapse all visible rows
  const toggleAllRows = () => {
    const visibleFailureIds = paginatedFailures.map((f) => f.failure_id);
    const allVisibleExpanded = visibleFailureIds.every((id) => expandedRows.has(id));

    if (allVisibleExpanded) {
      // Collapse all visible
      const newExpanded = new Set(expandedRows);
      visibleFailureIds.forEach((id) => newExpanded.delete(id));
      setExpandedRows(newExpanded);
    } else {
      // Expand all visible
      const currentExpanded = Array.from(expandedRows);
      setExpandedRows(new Set([...currentExpanded, ...visibleFailureIds]));
    }
  };

  // Table-only filter handlers (don't affect analysis)
  const handleTableColumnFilter = (column: keyof typeof tableFilters, values: string[]) => {
    setTableFilters((prev) => ({
      ...prev,
      [column]: values,
    }));
    setCurrentTablePage(1); // Reset pagination
  };

  const handleTableRangeFilter = (min: number | undefined, max: number | undefined) => {
    setTableFilters((prev) => ({
      ...prev,
      operationTimeMin: min,
      operationTimeMax: max,
    }));
    setCurrentTablePage(1);
  };

  const handleTableDateRangeFilter = (from: string | undefined, to: string | undefined) => {
    setTableFilters((prev) => ({
      ...prev,
      dateFrom: from,
      dateTo: to,
    }));
    setCurrentTablePage(1);
  };

  const handleSort = (key: string) => {
    setSortConfig((prevSort) => {
      if (prevSort?.key === key) {
        // 3-state toggle: asc -> desc -> null -> asc
        if (prevSort.direction === 'asc') {
          return { key, direction: 'desc' };
        } else if (prevSort.direction === 'desc') {
          return null; // No sorting
        }
      }
      // New column or returning from null, default to ascending
      return { key, direction: 'asc' };
    });
  };

  // Handler for analysis configuration changes
  const handleAnalysisConfigChange = (key: string, value: boolean | number) => {
    // Update local state with proper type handling
    if (key === 'showConfidenceIntervals' && typeof value === 'boolean') {
      setShowConfidenceIntervals(value);
    }
    if (key === 'confidenceLevel' && typeof value === 'number') {
      setConfidenceLevel(value);
    }
    if (key === 'showCensoredData' && typeof value === 'boolean') {
      setShowCensoredData(value);
    }

    // Mark workspace context as changed
    workspaceContextActions.markWorkspaceAsChanged();
  };

  // Auto-enable legend when plot customization is used
  const handleColorByChange = (value: ColorByOption) => {
    setColorBy(value);
    if (value !== 'none') {
      setShowLegend(true);
    }
  };

  const handleMarkerByChange = (value: MarkerByOption) => {
    setMarkerBy(value);
    if (value !== 'none') {
      setShowLegend(true);
    }
  };

  const handleSizeByChange = (value: SizeByOption) => {
    setSizeBy(value);
    if (value !== 'uniform') {
      setShowLegend(true);
    }
  };

  // Handle table pagination (UI-level)
  const handleTablePageChange = (page: number) => {
    setCurrentTablePage(page);
  };

  const handleNewWorkspace = () => {
    // Reset to default filters and analysis config
    setFilters(DEFAULT_FAILURE_FILTERS);
    setShowConfidenceIntervals(true);
    setConfidenceLevel(95);
    setShowCensoredData(true);
    setCurrentTablePage(1);
    setExpandedRows(new Set());
    setTableFilters({
      sites: [],
      turbineModels: [],
      componentsMajor: [],
      componentsMinor: [],
      failureModes: [],
      outcomeName: [],
      majorCorrective: [],
      plannedReactive: [],
      operationTimeMin: undefined,
      operationTimeMax: undefined,
      dateFrom: undefined,
      dateTo: undefined,
    });
    setSortConfig(null);

    // Clear workspace context
    workspaceContextActions.clearWorkspaceContext();
  };

  const handleSaveAnalysis = () => {
    // Open save modal - always saves complete dataset and cached results
    // No user options needed - simplified professional workflow
    setShowSaveModal(true);
  };

  const handleLoadAnalysis = () => {
    setShowLoadModal(true);
  };

  const handleSaveSuccess = (workspace: Workspace) => {
    // Update workspace context after successful save
    workspaceContextActions.updateWorkspaceAfterSave(workspace);

    // Could show toast notification here
  };

  const handleExportAnalysis = () => {
    // TODO: Implement export functionality
  };

  // Keyboard shortcuts
  useKeyboardShortcuts({
    onSave: handleSaveAnalysis,
    onLoad: handleLoadAnalysis,
    onNew: handleNewWorkspace,
    onExport: handleExportAnalysis,
  });

  // Only show full-screen loading on initial page load (filter options)
  if (isLoadingOptions) {
    return (
      <WaveLoading
        fullScreen={true}
        size="xl"
        variant="wave"
        color="bright-blue"
        text="Loading filter options..."
      />
    );
  }

  if (failuresError) {
    return (
      <WorkspaceErrorBoundary>
        <div className="min-h-screen flex items-center justify-center">
          <div className="alert alert-error max-w-md">
            <div>
              <h3 className="onyx-subtitle">Error loading failure data</h3>
              <div className="onyx-caption">Please try refreshing the page</div>
            </div>
          </div>
        </div>
      </WorkspaceErrorBoundary>
    );
  }

  return (
    <WorkspaceErrorBoundary>
      <div className="min-h-screen bg-gray-50">
        {/* Tool Page Header */}
        <PageToolbar
          title="Weibull Analysis"
          icon={ChartBarIcon}
          buttons={[
            {
              key: 'new-workspace',
              label: 'New Workspace',
              icon: ToolbarIcons.NewWorkspace,
              onClick: handleNewWorkspace,
              variant: 'secondary',
              tooltip: 'Start a new workspace (clears current filters and data)'
            },
            {
              key: 'load-workspace',
              label: 'Load Workspace',
              icon: ToolbarIcons.LoadWorkspace,
              onClick: handleLoadAnalysis,
              variant: 'secondary',
              tooltip: 'Load a saved workspace'
            },
            {
              key: 'save-workspace',
              label: workspaceContext.isWorkspaceLoaded ?
                (permissions.canEditWorkspace(workspaceContext.currentWorkspace as MinimalWorkspace) ? 'Update Workspace' : 'Save as New') :
                'Save Workspace',
              icon: ToolbarIcons.SaveWorkspace,
              onClick: handleSaveAnalysis,
              variant: 'primary',
              tooltip: workspaceContext.isWorkspaceLoaded ?
                (permissions.canEditWorkspace(workspaceContext.currentWorkspace as MinimalWorkspace) ?
                  'Update current workspace' :
                  'You cannot edit this workspace - will save as new') :
                'Save current analysis as workspace'
            }
          ]}
          statusInfo={{
            workspaceName: workspaceContext.currentWorkspace.name,
            hasUnsavedChanges: workspaceContext.currentWorkspace.hasUnsavedChanges
          }}
        />

        {/* Main Content Layout */}
        <div className="flex flex-1">
          {/* Stackable Sidebars */}
          <SidebarController
            // Filter props
            filters={filters}
            onFiltersChange={handleFiltersChange}
            filterOptions={
              apiFilterOptions || {
                sites: [],
                turbineModels: [],
                componentsMajor: [],
                componentsMinor: [],
                failureModes: [],
                outcomeName: [],
                excludedCaseIds: [],
              }
            }
            isLoading={false}
            // Analysis settings props
            plotType={plotType}
            onPlotTypeChange={setPlotType}
            showConfidenceIntervals={showConfidenceIntervals}
            confidenceLevel={confidenceLevel}
            onToggleConfidenceIntervals={(value) =>
              handleAnalysisConfigChange('showConfidenceIntervals', value)
            }
            onConfidenceLevelChange={(value) =>
              handleAnalysisConfigChange('confidenceLevel', value)
            }
            showCensoredData={showCensoredData}
            onToggleCensoredData={(value) =>
              handleAnalysisConfigChange('showCensoredData', value)
            }
            selectedMethod={selectedMethod}
            onMethodChange={setSelectedMethod}
            // Plot customization props
            colorBy={colorBy}
            onColorByChange={handleColorByChange}
            markerBy={markerBy}
            onMarkerByChange={handleMarkerByChange}
            sizeBy={sizeBy}
            onSizeByChange={handleSizeByChange}
            showLegend={showLegend}
            onShowLegendChange={setShowLegend}
            // Weibull-specific props
            isVblAnalysis={false}
          />

          {/* Main Content Area */}
          <div className="flex-1 overflow-y-auto min-w-0">
            <div style={getContentAreaStyle()}>
              {/* Summary Statistics - Using ResponsiveGrid */}
              <ResponsiveGrid minCardWidth={250} gap="md" className="mb-6">
                <StatsCard
                  title="Total Failures"
                  value={metadata?.failureCount || 0}
                  description="Fleet-wide failure records (not censored)"
                  color="bright-blue"
                  loading={isWeibullLoading}
                />

                <StatsCard
                  title="Avg Operation Time"
                  value={weibullAnalysis ? `${weibullAnalysis.summary.meanLife.toFixed(1)} years` : '—'}
                  description="Mean time to failure"
                  color="green"
                  loading={isWeibullLoading}
                />

                <StatsCard
                  title="Selected for Analysis"
                  value={metadata?.totalSamples || 0}
                  description={
                    metadata?.hasCensoredData
                      ? `${metadata.failureCount} failures, ${metadata.censoredCount} censored`
                      : `${metadata?.failureCount || 0} failure records`
                  }
                  color="purple"
                  loading={isWeibullLoading}
                />

                <StatsCard
                  title="Sites"
                  value={diversityMetrics?.uniqueSites || 0}
                  description="Wind farm locations"
                  color="fleet-monitor-blue"
                  loading={isWeibullLoading}
                />
              </ResponsiveGrid>

              {/* Grouped Parameter Cards */}
              {weibullAnalysis && (
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
                  {/* Weibull Parameters */}
                  <ParameterGroupCard
                    title="Weibull Parameters"
                    variant="weibull"
                    helpText={`Weibull Parameters for Reliability Analysis:

Shape (β): Characterizes failure rate behavior
Scale (η): Characteristic life at 63.2% probability`}
                    parameters={[
                      {
                        label: 'Shape Parameter (β)',
                        value: weibullAnalysis.parameters.shape || 0,
                        ci: weibullAnalysis.parameters.shapeCI ?
                          [weibullAnalysis.parameters.shapeCI.lower, weibullAnalysis.parameters.shapeCI.upper] : undefined,
                        skipInterpretation: true,
                      },
                      {
                        label: 'Shape SE',
                        value: weibullAnalysis.statistics?.standardErrors?.shape ||
                          (weibullAnalysis.parameters.shapeCI ?
                            (weibullAnalysis.parameters.shapeCI.upper - weibullAnalysis.parameters.shapeCI.lower) / (2 * 1.96) : 0),
                        skipInterpretation: true,
                      },
                      {
                        label: 'Scale Parameter (η)',
                        value: weibullAnalysis.parameters.scale || 0,
                        unit: 'years',
                        ci: weibullAnalysis.parameters.scaleCI ?
                          [weibullAnalysis.parameters.scaleCI.lower, weibullAnalysis.parameters.scaleCI.upper] : undefined,
                      },
                      {
                        label: 'Scale SE',
                        value: weibullAnalysis.statistics?.standardErrors?.scale ||
                          (weibullAnalysis.parameters.scaleCI ?
                            (weibullAnalysis.parameters.scaleCI.upper - weibullAnalysis.parameters.scaleCI.lower) / (2 * 1.96) : 0),
                        unit: 'years',
                        skipInterpretation: true,
                      },
                    ]}
                    compact={false}
                  />

                  {/* Life Characteristics */}
                  <ParameterGroupCard
                    title="Life Characteristics"
                    variant="life"
                    helpText={`Life metrics at different failure probabilities:

MTTF: Mean Time To Failure
B10: Time at which 10% have failed
B50: Median life (50% failures)
B63.2: Characteristic life (scale parameter)`}
                    parameters={[
                      {
                        label: 'Mean Life (MTTF)',
                        value: weibullAnalysis.summary.meanLife || 0,
                        unit: 'years',
                        skipInterpretation: true,
                        description: 'Expected component lifetime',
                      },
                      {
                        label: 'B10 Life',
                        value: weibullAnalysis.parameters.scale * Math.pow(-Math.log(0.9), 1 / weibullAnalysis.parameters.shape),
                        unit: 'years',
                        skipInterpretation: true,
                        description: '10% failure probability',
                      },
                      {
                        label: 'Median Life (B50)',
                        value: weibullAnalysis.summary.medianLife ||
                          (weibullAnalysis.parameters.scale * Math.pow(-Math.log(0.5), 1 / weibullAnalysis.parameters.shape)),
                        unit: 'years',
                        ci: weibullAnalysis.parameters.scaleCI ? [
                          weibullAnalysis.parameters.scaleCI.lower * Math.pow(-Math.log(0.5), 1 / weibullAnalysis.parameters.shape),
                          weibullAnalysis.parameters.scaleCI.upper * Math.pow(-Math.log(0.5), 1 / weibullAnalysis.parameters.shape)
                        ] : undefined,
                        skipInterpretation: true,
                        description: '50% failure probability',
                      },
                      {
                        label: 'Characteristic Life',
                        value: weibullAnalysis.parameters.scale,
                        unit: 'years',
                        ci: weibullAnalysis.parameters.scaleCI ?
                          [weibullAnalysis.parameters.scaleCI.lower, weibullAnalysis.parameters.scaleCI.upper] : undefined,
                        description: '63.2% failure probability',
                      },
                    ]}
                    compact={false}
                  />

                  {/* Model Fit */}
                  <ParameterGroupCard
                    title="Model Fit"
                    variant="fit"
                    helpText={`Model Quality Indicators:

R² > 0.95: Excellent fit
R² > 0.90: Good fit
R² > 0.80: Fair fit
R² < 0.80: Poor fit

Adjusted R²: Accounts for sample size
RMSE: Root Mean Square Error`}
                    parameters={[
                      {
                        label: 'Goodness of Fit (R²)',
                        value: weibullAnalysis.parameters.rSquared || 0,
                      },
                      {
                        label: 'Adjusted R²',
                        value: (() => {
                          const r2 = weibullAnalysis.parameters.rSquared || 0;
                          const n = weibullAnalysis.summary.sampleSize || metadata?.totalSamples || 0;
                          const k = 2; // 2 parameters for Weibull
                          return n > k ? 1 - ((1 - r2) * (n - 1) / (n - k)) : r2;
                        })(),
                        skipInterpretation: true,
                      },
                      {
                        label: 'RMSE',
                        value: Math.sqrt(1 - (weibullAnalysis.parameters.rSquared || 0)) * 0.5,
                        skipInterpretation: true,
                      },
                      {
                        label: 'Sample Size',
                        value: `${metadata?.totalSamples || weibullAnalysis.summary.sampleSize || 0} points`,
                        skipInterpretation: true,
                      },
                      {
                        label: 'Method',
                        value: selectedMethod.toUpperCase(),
                        skipInterpretation: true,
                      },
                    ]}
                    compact={false}
                  />
                </div>
              )}

              {/* Weibull Analysis - Single or Dual Plot based on viewport */}
              {showDualPlots ? (
                <DualPlotView
                  analysis={weibullAnalysis}
                  isLoading={isWeibullLoading}
                  showConfidenceIntervals={showConfidenceIntervals}
                  confidenceLevel={confidenceLevel}
                  showCensoredData={showCensoredData}
                  censoredData={censoredAnalysis?.plotData.censored}
                  selectedMethod={selectedMethod}
                  colorBy={colorBy}
                  markerBy={markerBy}
                  sizeBy={sizeBy}
                  showLegend={showLegend}
                />
              ) : (
                <div className="mb-6">
                  <WeibullAnalysisPanel
                    analysis={weibullAnalysis}
                    isLoading={isWeibullLoading}
                    hasEnoughData={hasEnoughData}
                    sampleSize={sampleSize}
                    showConfidenceIntervals={showConfidenceIntervals}
                    confidenceLevel={confidenceLevel}
                    showCensoredData={showCensoredData}
                    metadata={metadata}
                    censoredData={censoredAnalysis?.plotData.censored}
                    selectedMethod={selectedMethod}
                    plotType={plotType}
                    showSettingsInSidebar={true}
                    colorBy={colorBy}
                    markerBy={markerBy}
                    sizeBy={sizeBy}
                    showLegend={showLegend}
                  />
                </div>
              )}

              {/* Failure Records Table */}
              <div className="mb-6">
                <TableCard
                  title="Failure Records"
                  overflow='visible'
                  headerActions={
                    <div className="flex items-center gap-4">
                      <button
                        onClick={handleSelectAll}
                        disabled={isLoadingFailures}
                        className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 disabled:opacity-50"
                      >
                        {(() => {
                          const visibleCaseIds = Array.from(
                            new Set(paginatedFailures.map((f) => f.case_id.toString()))
                          );
                          const allVisibleExcluded = visibleCaseIds.every((id) =>
                            (filters.excludedCaseIds || []).includes(id)
                          );
                          return allVisibleExcluded ? 'Select All' : 'Deselect All';
                        })()}
                      </button>
                      <button
                        onClick={toggleAllRows}
                        disabled={isLoadingFailures}
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                      >
                        {(() => {
                          const visibleFailureIds = paginatedFailures.map((f) => f.failure_id);
                          const allVisibleExpanded = visibleFailureIds.every((id) =>
                            expandedRows.has(id)
                          );
                          return allVisibleExpanded ? 'Collapse All' : 'Expand All';
                        })()}
                      </button>
                      <div className="text-sm text-gray-600">
                        {isLoadingFailures ? (
                          <WaveLoading size="sm" variant="dots" color="bright-blue" />
                        ) : (
                          `${paginatedFailures.length} shown • ${tablePaginationData.includedCount} included • ${completeFailures.length} total`
                        )}
                      </div>
                    </div>
                  }
                  footerActions={
                    !isLoadingFailures && tablePaginationData.totalPages > 1 ? (
                      <div className="flex items-center justify-between w-full">
                        <div className="flex-1 flex justify-between sm:hidden">
                          <button
                            onClick={() => handleTablePageChange(Math.max(1, currentTablePage - 1))}
                            disabled={currentTablePage === 1}
                            className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                          >
                            Previous
                          </button>
                          <button
                            onClick={() => handleTablePageChange(currentTablePage + 1)}
                            disabled={!tablePaginationData.hasMore}
                            className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                          >
                            Next
                          </button>
                        </div>
                        <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                          <div>
                            <p className="text-sm text-gray-700">
                              Showing{' '}
                              <span className="font-medium">
                                {(currentTablePage - 1) * tablePageSize + 1}
                              </span>{' '}
                              to{' '}
                              <span className="font-medium">
                                {Math.min(
                                  currentTablePage * tablePageSize,
                                  tablePaginationData.totalCount
                                )}
                              </span>{' '}
                              of <span className="font-medium">{tablePaginationData.totalCount}</span>{' '}
                              cases ({tablePaginationData.includedCount} included in analysis)
                            </p>
                          </div>
                          <div>
                            <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                              <button
                                onClick={() => handleTablePageChange(Math.max(1, currentTablePage - 1))}
                                disabled={currentTablePage === 1}
                                className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                              >
                                Previous
                              </button>
                              <span className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700">
                                Page {currentTablePage} of {tablePaginationData.totalPages}
                              </span>
                              <button
                                onClick={() => handleTablePageChange(currentTablePage + 1)}
                                disabled={!tablePaginationData.hasMore}
                                className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                              >
                                Next
                              </button>
                            </nav>
                          </div>
                        </div>
                      </div>
                    ) : undefined
                  }
                  loading={isLoadingFailures}
                >
                  {!isLoadingFailures && (
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200 table-fixed">
                        <thead className="bg-white border-b border-gray-100">
                          <tr>
                            <th className="px-6 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-12">
                              <input
                                type="checkbox"
                                checked={(() => {
                                  const visibleCaseIds = Array.from(
                                    new Set(paginatedFailures.map((f) => f.case_id.toString()))
                                  );
                                  const anyExcluded = visibleCaseIds.some((id: string) =>
                                    (filters.excludedCaseIds || []).includes(id)
                                  );
                                  return paginatedFailures.length > 0 && !anyExcluded;
                                })()}
                                onChange={handleSelectAllInPage}
                                className="rounded"
                              />
                            </th>

                            <ColumnHeader
                              title="Case ID"
                              sortKey="caseId"
                              sortDirection={sortConfig?.key === 'caseId' ? sortConfig.direction : null}
                              onSort={handleSort}
                              width="w-24"
                            />

                            <ColumnHeader
                              title="Turbine"
                              sortKey="turbine"
                              sortDirection={sortConfig?.key === 'turbine' ? sortConfig.direction : null}
                              onSort={handleSort}
                              filterType="multiselect"
                              filterOptions={apiFilterOptions?.turbineModels || []}
                              selectedValues={tableFilters.turbineModels}
                              onFilterChange={(values) =>
                                handleTableColumnFilter('turbineModels', values)
                              }
                              width="w-28"
                            />

                            <ColumnHeader
                              title="Site"
                              sortKey="site"
                              sortDirection={sortConfig?.key === 'site' ? sortConfig.direction : null}
                              onSort={handleSort}
                              filterType="multiselect"
                              filterOptions={apiFilterOptions?.sites || []}
                              selectedValues={tableFilters.sites}
                              onFilterChange={(values) => handleTableColumnFilter('sites', values)}
                              width="w-28"
                            />

                            <ColumnHeader
                              title="Component"
                              sortKey="component"
                              sortDirection={
                                sortConfig?.key === 'component' ? sortConfig.direction : null
                              }
                              onSort={handleSort}
                              filterType="multiselect"
                              filterOptions={[
                                ...(apiFilterOptions?.componentsMajor || []),
                                ...(apiFilterOptions?.componentsMinor || []),
                              ]}
                              selectedValues={[
                                ...tableFilters.componentsMajor,
                                ...tableFilters.componentsMinor,
                              ]}
                              onFilterChange={(values) => {
                                // Split values between major and minor components
                                const majorComponents = values.filter((v) =>
                                  apiFilterOptions?.componentsMajor?.includes(v)
                                );
                                const minorComponents = values.filter((v) =>
                                  apiFilterOptions?.componentsMinor?.includes(v)
                                );
                                setTableFilters((prev) => ({
                                  ...prev,
                                  componentsMajor: majorComponents,
                                  componentsMinor: minorComponents,
                                }));
                                setCurrentTablePage(1);
                              }}
                              width="w-32"
                            />

                            <ColumnHeader
                              title="Failure Mode"
                              sortKey="failureMode"
                              sortDirection={
                                sortConfig?.key === 'failureMode' ? sortConfig.direction : null
                              }
                              onSort={handleSort}
                              filterType="searchable"
                              filterOptions={apiFilterOptions?.failureModes || []}
                              selectedValues={tableFilters.failureModes}
                              onFilterChange={(values) => handleTableColumnFilter('failureModes', values)}
                              width="w-36"
                            />

                            <ColumnHeader
                              title="Type"
                              sortKey="type"
                              sortDirection={sortConfig?.key === 'type' ? sortConfig.direction : null}
                              onSort={handleSort}
                              filterType="multiselect"
                              filterOptions={apiFilterOptions?.outcomeName || []}
                              selectedValues={tableFilters.outcomeName}
                              onFilterChange={(values) => handleTableColumnFilter('outcomeName', values)}
                              width="w-24"
                            />

                            <ColumnHeader
                              title='Major Corrective'
                              sortKey='majorCorrective'
                              sortDirection={sortConfig?.key === 'majorCorrective' ? sortConfig.direction : null}
                              onSort={handleSort}
                              filterType='multiselect'
                              filterOptions={apiFilterOptions?.majorCorrective || ["Yes", "No", "N/A"]}
                              selectedValues={tableFilters.majorCorrective}
                              onFilterChange={(values) => handleTableColumnFilter('majorCorrective', values)}
                              width='w-16'
                            />

                            <ColumnHeader
                              title='Planned / Reactive'
                              sortKey='plannedReactive'
                              sortDirection={sortConfig?.key === 'plannedReactive' ? sortConfig.direction : null}
                              onSort={handleSort}
                              filterType='multiselect'
                              filterOptions={apiFilterOptions?.plannedReactive || ["Planned", "Reactive", "N/A"]}
                              selectedValues={tableFilters.plannedReactive}
                              onFilterChange={(values) => handleTableColumnFilter('plannedReactive', values)}
                              width='w-28'
                            />


                            <ColumnHeader
                              title="Operation Time (Years)"
                              sortKey="operationTime"
                              sortDirection={
                                sortConfig?.key === 'operationTime' ? sortConfig.direction : null
                              }
                              onSort={handleSort}
                              filterType="range"
                              rangeMin={tableFilters.operationTimeMin}
                              rangeMax={tableFilters.operationTimeMax}
                              onRangeChange={handleTableRangeFilter}
                              width="w-28"
                            />

                            <ColumnHeader
                              title="Failure Date"
                              sortKey="failureDate"
                              sortDirection={
                                sortConfig?.key === 'failureDate' ? sortConfig.direction : null
                              }
                              onSort={handleSort}
                              filterType="daterange"
                              dateFrom={tableFilters.dateFrom}
                              dateTo={tableFilters.dateTo}
                              onDateRangeChange={handleTableDateRangeFilter}
                              width="w-24"
                            />

                            <th className="px-2 py-2 text-center w-8">
                              {/* Details column - no text, just space for expand/collapse buttons */}
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {paginatedFailures.map((failure: FailureRecord) => {
                            const isExcluded = (filters.excludedCaseIds || []).includes(
                              failure.case_id.toString()
                            );
                            const isExpanded = expandedRows.has(failure.failure_id);
                            return (
                              <React.Fragment key={failure.failure_id}>
                                {/* Main compact row with truncated content */}
                                <tr
                                  className={`hover:bg-gray-50 ${!isExcluded ? 'bg-blue-50' : ''} ${isExpanded ? 'border-b-0' : ''}`}
                                >
                                  <td className="px-6 py-4 whitespace-nowrap w-12">
                                    <input
                                      type="checkbox"
                                      checked={!isExcluded}
                                      onChange={() => handleSelectFailure(failure.failure_id)}
                                      className="rounded"
                                    />
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-gray-900 w-24">
                                    {failure.case_id ? (
                                      <a
                                        href={externalLinks.aiHub.caseManagement(failure.case_id)}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-800 hover:underline transition-colors"
                                        title={`View case ${failure.case_id} in AI Hub`}
                                      >
                                        <span className="truncate max-w-[60px]">
                                          {failure.case_id}
                                        </span>
                                      </a>
                                    ) : (
                                      <div className="truncate max-w-[80px] text-gray-400">
                                        N/A
                                      </div>
                                    )}
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 w-28">
                                    <div className="truncate max-w-[96px]" title={failure.turbine_name}>
                                      {failure.turbine_name}
                                    </div>
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 w-28">
                                    <div className="truncate max-w-[96px]" title={failure.site_name}>
                                      {failure.site_name}
                                    </div>
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 w-28">
                                    {(() => {
                                      const fullComponent =
                                        failure.component_major +
                                        (failure.component_minor ? ` (${failure.component_minor})` : '');
                                      return (
                                        <div className="truncate max-w-[112px]" title={fullComponent}>
                                          {fullComponent}
                                        </div>
                                      );
                                    })()}
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 w-36">
                                    <div className="truncate max-w-[144px]" title={failure.failure_mode}>
                                      {failure.failure_mode}
                                    </div>
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap w-24">
                                    <span
                                      className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${failure.outcome_name.includes('Replace')
                                        ? 'bg-red-100 text-red-800'
                                        : failure.outcome_name.includes('Planned')
                                          ? 'bg-blue-100 text-blue-800'
                                          : 'bg-yellow-100 text-yellow-800'
                                        }`}
                                    >
                                      <div className="truncate max-w-[60px]" title={failure.outcome_name}>
                                        {failure.outcome_name}
                                      </div>
                                    </span>
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 w-24">
                                    <div className="truncate max-w-[52px]" title={failure.major_corrective}>
                                      {failure.major_corrective}
                                    </div>
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap w-24">
                                    <span
                                      className={`inline-flex px-2 py-1 ${failure.planned_reactive?.includes('Reactive')
                                        ? 'text-xs font-semibold rounded-full bg-yellow-100 text-yellow-800'
                                        : failure.planned_reactive?.includes('Planned')
                                          ? 'text-xs font-semibold rounded-full bg-blue-100 text-blue-800'
                                          : 'text-sm text-gray-500'
                                        }`}
                                    >
                                      <div className="truncate max-w-[60px]" title={failure.planned_reactive}>
                                        {failure.planned_reactive}
                                      </div>
                                    </span>
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-mono w-28">
                                    {failure.operation_time_years.toFixed(2)}
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 w-24">
                                    {new Date(failure.failure_date).toLocaleDateString()}
                                  </td>
                                  <td className="px-2 py-4 whitespace-nowrap w-8 text-center">
                                    <button
                                      onClick={() => toggleRowExpansion(failure.failure_id)}
                                      className="p-1 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded transition-colors duration-200"
                                      title={isExpanded ? 'Collapse details' : 'Expand details'}
                                    >
                                      <ChevronDownIcon
                                        className={`h-4 w-4 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''
                                          }`}
                                      />
                                    </button>
                                  </td>
                                </tr>

                                {/* Expanded details row */}
                                {isExpanded && (
                                  <tr
                                    className={`${!isExcluded ? 'bg-blue-50' : 'bg-gray-50'} border-b border-gray-200`}
                                  >
                                    <td colSpan={12} className="px-6 py-4">
                                      <div className="bg-white rounded-lg border border-gray-100 p-4 shadow-sm">
                                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 text-sm">
                                          <div>
                                            <span className="font-medium text-gray-600">
                                              Full Turbine:
                                            </span>
                                            <div className="mt-1 text-gray-900">
                                              {failure.turbine_name}
                                            </div>
                                          </div>
                                          <div>
                                            <span className="font-medium text-gray-600">
                                              Site Location:
                                            </span>
                                            <div className="mt-1 text-gray-900">{failure.site_name}</div>
                                          </div>
                                          <div>
                                            <span className="font-medium text-gray-600">
                                              Component Details:
                                            </span>
                                            <div className="mt-1 text-gray-900">
                                              {failure.component_major}
                                              {failure.component_minor && (
                                                <span className="text-gray-600">
                                                  {' '}
                                                  ({failure.component_minor})
                                                </span>
                                              )}
                                            </div>
                                          </div>
                                          <div>
                                            <span className="font-medium text-gray-600">
                                              Failure Mode:
                                            </span>
                                            <div className="mt-1 text-gray-900">
                                              {failure.failure_mode}
                                            </div>
                                          </div>
                                          <div>
                                            <span className="font-medium text-gray-600">
                                              Outcome Type:
                                            </span>
                                            <div className="mt-1">
                                              <span
                                                className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${failure.outcome_name.includes('Replace')
                                                  ? 'bg-red-100 text-red-800'
                                                  : failure.outcome_name.includes('Planned')
                                                    ? 'bg-blue-100 text-blue-800'
                                                    : 'bg-yellow-100 text-yellow-800'
                                                  }`}
                                              >
                                                {failure.outcome_name}
                                              </span>
                                            </div>
                                          </div>
                                          <div>
                                            <span className="font-medium text-gray-600">
                                              Operation Details:
                                            </span>
                                            <div className="mt-1 text-gray-900">
                                              {failure.operation_time_years.toFixed(2)} years
                                              <span className="text-gray-600 ml-2">
                                                ({new Date(failure.failure_date).toLocaleDateString()})
                                              </span>
                                            </div>
                                          </div>
                                          <div>
                                            <span className="font-medium text-gray-600">
                                              Major Corrective:
                                            </span>
                                            <div className="mt-1 text-gray-900">
                                              {failure.major_corrective}
                                            </div>
                                          </div>
                                          <div>
                                            <span className="font-medium text-gray-600">
                                              Planned / Reactive:
                                            </span>
                                            <div className="mt-1 text-gray-900">
                                              {failure.planned_reactive}
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                    </td>
                                  </tr>
                                )}
                              </React.Fragment>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </TableCard>
              </div>
            </div>
          </div>
        </div>

        {/* Workspace Modals */}
        <SaveWorkspaceModal
          isOpen={showSaveModal}
          onClose={() => setShowSaveModal(false)}
          currentFilters={filters}
          currentAnalysisConfig={{
            showConfidenceIntervals,
            confidenceLevel,
            showCensoredData,
          }}
          currentFailures={completeFailures}
          currentResults={weibullAnalysis}
          onSuccess={handleSaveSuccess}
          workspaceContext={workspaceContext}
          workspaceType="weibull"
        />

        <LoadWorkspaceModal
          isOpen={showLoadModal}
          onClose={() => setShowLoadModal(false)}
          onLoadWorkspace={handleLoadWorkspace}
          workspaceType="weibull"
        />
      </div>
    </WorkspaceErrorBoundary>
  );
};

export default Analysis;